/**
 * Google Play app detection (Trusted Web Activity). Google Play's Payments policy forbids leading
 * users of a Play-distributed app to a payment method other than Play Billing (outside enrolled
 * regional programs), so the web checkout must never be reachable from inside the Android app.
 *
 * Signals, read once at start-up: the TWA opens the start URL with `?source=android-app`
 * (android/twa.config.json) and Chrome sets `document.referrer` to `android-app://<package>/`.
 * The flag lives in sessionStorage, not localStorage: a TWA shares storage with Chrome for the
 * same origin, and a persistent flag would wrongly mark later visits from the regular browser.
 */
const KEY = 'pa.androidApp';

export function detectAndroidApp(
  href: string = location.href,
  referrer: string = document.referrer,
  store: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage,
): boolean {
  const fromApp =
    referrer.startsWith('android-app://') ||
    new URL(href).searchParams.get('source') === 'android-app';
  try {
    if (fromApp) store.setItem(KEY, '1');
    return fromApp || store.getItem(KEY) === '1';
  } catch {
    return fromApp;
  }
}

let cached: boolean | undefined;

/** True when running inside the Google Play Android app. */
export function isAndroidApp(): boolean {
  cached ??= detectAndroidApp();
  return cached;
}
